import type { BrouillonIntervention, ErreurSaisieIntervention } from './intervention-edition';

/** Référence de validation locale, indépendante du navigateur et absente des requêtes HTTP. */
const FUSEAU_INTERVENTION = 'Europe/Paris';

const formatLocal = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU_INTERVENTION,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Date et heure civiles dans le fuseau de planification, au format YYYY-MM-DDTHH:mm. */
function dateHeureLocale(instant: Date): string {
  const parties = Object.fromEntries(
    formatLocal.formatToParts(instant).map((p) => [p.type, p.value]),
  );
  return `${parties['year'].padStart(4, '0')}-${parties['month']}-${parties['day']}T${parties['hour']}:${parties['minute']}`;
}

/** Jour minimal proposé par le champ date ; la validation contrôle aussi l’heure exacte. */
export function jourPlanification(instant = new Date()): string {
  return dateHeureLocale(instant).slice(0, 10);
}

/**
 * Résout une heure civile dans le fuseau de validation. Les décalages de la veille et du lendemain
 * permettent de détecter les heures absentes ou répétées lors d’un changement d’heure.
 */
function instantsPossibles(date: string, heure: string): number[] {
  const local = `${date}T${heure}`;
  const reference = Date.parse(`${local}:00Z`);
  const decalages = new Set(
    [-1, 0, 1].map((jour) => {
      const sonde = reference + jour * 24 * 60 * 60 * 1000;
      return Date.parse(`${dateHeureLocale(new Date(sonde))}:00Z`) - sonde;
    }),
  );
  return [...decalages]
    .map((decalage) => reference - decalage)
    .filter((instant) => Number.isFinite(instant) && dateHeureLocale(new Date(instant)) === local);
}

/** Vérifie la date et les heures facultatives ; compare le créneau uniquement s’il est complet. */
export function validerDatesPlanification(
  valeurs: Pick<BrouillonIntervention, 'date' | 'debut' | 'fin'>,
  maintenant = new Date(),
): ErreurSaisieIntervention[] {
  const erreurs: ErreurSaisieIntervention[] = [];
  const date = new Date(`${valeurs.date}T00:00:00Z`);
  // Date normalise certaines dates impossibles : le retour ISO doit donner le jour saisi.
  const dateValide =
    /^\d{4}-\d{2}-\d{2}$/.test(valeurs.date) &&
    Number.isFinite(date.getTime()) &&
    date.toISOString().slice(0, 10) === valeurs.date;
  if (!dateValide) {
    erreurs.push({ champ: 'date', message: 'Renseignez une date valide.' });
  } else if (valeurs.date < jourPlanification(maintenant)) {
    erreurs.push({ champ: 'date', message: 'La date de l’intervention ne peut pas être passée.' });
  }

  const heureValide = (heure: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(heure);
  for (const champ of ['debut', 'fin'] as const) {
    // Chaque heure peut être omise indépendamment de l’autre, sans inventer d’horaire par défaut.
    if (!valeurs[champ]) continue;
    if (!heureValide(valeurs[champ])) {
      erreurs.push({
        champ,
        message:
          champ === 'debut'
            ? 'Renseignez une heure de début valide.'
            : 'Renseignez une heure de fin valide.',
      });
    } else if (dateValide && !erreurs.some((erreur) => erreur.champ === 'date')) {
      const instants = instantsPossibles(valeurs.date, valeurs[champ]);
      if (instants.length !== 1) {
        erreurs.push({
          champ,
          message:
            instants.length === 0
              ? 'Cette heure n’existe pas à Paris ce jour-là (changement d’heure). Choisissez un autre horaire.'
              : 'Cette heure existe deux fois à Paris ce jour-là (changement d’heure). Choisissez un horaire non ambigu.',
        });
      } else if (champ === 'debut' && instants[0] <= maintenant.getTime()) {
        erreurs.push({ champ, message: 'L’heure de début doit être dans le futur.' });
      } else if (champ === 'fin' && !valeurs.debut && instants[0] <= maintenant.getTime()) {
        // Même sans début, une fin déjà passée ne décrit pas une intervention à venir.
        erreurs.push({ champ, message: 'L’heure de fin doit être dans le futur.' });
      }
    }
  }
  if (
    heureValide(valeurs.debut) &&
    heureValide(valeurs.fin) &&
    valeurs.fin <= valeurs.debut &&
    !erreurs.some((erreur) => erreur.champ === 'fin')
  ) {
    erreurs.push({ champ: 'fin', message: 'La fin doit être postérieure au début.' });
  }
  return erreurs;
}
